import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { policyClaimService } from '../services/PolicyClaimService.js';
import { policyKeys } from '@features/policies/hooks/usePoliciesQuery.js';
import type { CreatePolicyClaimDto, UpdatePolicyClaimDto } from '@repo/types';

export const claimKeys = {
  all: ['claims'] as const,
  byPolicy: (policyId: string) => ['claims', 'byPolicy', policyId] as const,
  detail: (policyId: string, claimId: string) => ['claims', 'detail', policyId, claimId] as const,
};

export function usePolicyClaimsQuery(policyId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: claimKeys.byPolicy(policyId),
    queryFn: () => policyClaimService.list(policyId),
    enabled: !!policyId && (options?.enabled ?? true),
  });
}

export function useCreatePolicyClaimMutation(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePolicyClaimDto) => policyClaimService.create(policyId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimKeys.byPolicy(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.detail(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.all });
    },
    meta: {
      successMessage: 'Claim recorded successfully',
    },
  });
}

export function useUpdatePolicyClaimMutation(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, data }: { claimId: string; data: UpdatePolicyClaimDto }) =>
      policyClaimService.update(policyId, claimId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimKeys.byPolicy(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.detail(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.all });
    },
    meta: {
      successMessage: 'Claim updated successfully',
    },
  });
}

export function useDeletePolicyClaimMutation(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) => policyClaimService.delete(policyId, claimId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimKeys.byPolicy(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.detail(policyId) });
      void queryClient.invalidateQueries({ queryKey: policyKeys.all });
    },
    meta: {
      successMessage: 'Claim deleted successfully',
    },
  });
}
